import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Mail, Bell, MessageSquare, DollarSign, Truck, Star, Megaphone, 
  Loader2, Save, RefreshCw
} from 'lucide-react';

/**
 * Notification Preferences component for Roadside Rescue
 * Allows users to control email and push notification settings
 */
export default function NotificationPreferences() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    // Email preferences
    email_payment_receipts: true,
    email_job_updates: true,
    email_provider_assigned: true,
    email_review_reminder: true,
    email_marketing: false,
    // Push preferences
    push_job_updates: true,
    push_provider_assigned: true,
    push_chat_messages: true
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const res = await api.get('/roadside/user/notification-preferences');
      if (res.data) {
        setPreferences(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await api.put('/roadside/user/notification-preferences', preferences);
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return (
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-blue-400" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Control how you receive updates about your roadside assistance requests
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Email Notifications */}
        <div>
          <h3 className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-4">
            <Mail className="h-4 w-4 text-slate-400" />
            Email Notifications
          </h3>
          
          <div className="space-y-4 pl-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DollarSign className="h-4 w-4 text-green-400" />
                <Label htmlFor="email_payment_receipts" className="cursor-pointer">
                  <span className="text-white">Payment Receipts</span>
                  <p className="text-xs text-slate-500">Receive email receipts after payments</p>
                </Label>
              </div>
              <Switch
                id="email_payment_receipts"
                checked={preferences.email_payment_receipts}
                onCheckedChange={() => handleToggle('email_payment_receipts')}
                data-testid="toggle-email-receipts"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Truck className="h-4 w-4 text-blue-400" />
                <Label htmlFor="email_job_updates" className="cursor-pointer">
                  <span className="text-white">Job Updates</span>
                  <p className="text-xs text-slate-500">Status changes for your requests</p>
                </Label>
              </div>
              <Switch
                id="email_job_updates"
                checked={preferences.email_job_updates}
                onCheckedChange={() => handleToggle('email_job_updates')}
                data-testid="toggle-email-job-updates"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Truck className="h-4 w-4 text-purple-400" />
                <Label htmlFor="email_provider_assigned" className="cursor-pointer">
                  <span className="text-white">Provider Assigned</span>
                  <p className="text-xs text-slate-500">When a provider accepts your request</p>
                </Label>
              </div>
              <Switch
                id="email_provider_assigned"
                checked={preferences.email_provider_assigned}
                onCheckedChange={() => handleToggle('email_provider_assigned')}
                data-testid="toggle-email-provider"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Star className="h-4 w-4 text-amber-400" />
                <Label htmlFor="email_review_reminder" className="cursor-pointer">
                  <span className="text-white">Review Reminders</span>
                  <p className="text-xs text-slate-500">Reminders to leave reviews after service</p>
                </Label>
              </div>
              <Switch
                id="email_review_reminder"
                checked={preferences.email_review_reminder}
                onCheckedChange={() => handleToggle('email_review_reminder')}
                data-testid="toggle-email-reviews"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Megaphone className="h-4 w-4 text-orange-400" />
                <Label htmlFor="email_marketing" className="cursor-pointer">
                  <span className="text-white">Marketing & Promotions</span>
                  <p className="text-xs text-slate-500">Special offers and news</p>
                </Label>
              </div>
              <Switch
                id="email_marketing"
                checked={preferences.email_marketing}
                onCheckedChange={() => handleToggle('email_marketing')}
                data-testid="toggle-email-marketing"
              />
            </div>
          </div>
        </div>
        
        <Separator className="bg-slate-800" />
        
        {/* Push Notifications */}
        <div>
          <h3 className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-4">
            <Bell className="h-4 w-4 text-slate-400" />
            Push Notifications
          </h3>
          
          <div className="space-y-4 pl-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Truck className="h-4 w-4 text-blue-400" />
                <Label htmlFor="push_job_updates" className="cursor-pointer">
                  <span className="text-white">Job Updates</span>
                  <p className="text-xs text-slate-500">Real-time status updates</p>
                </Label>
              </div>
              <Switch
                id="push_job_updates"
                checked={preferences.push_job_updates}
                onCheckedChange={() => handleToggle('push_job_updates')}
                data-testid="toggle-push-updates"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Truck className="h-4 w-4 text-purple-400" />
                <Label htmlFor="push_provider_assigned" className="cursor-pointer">
                  <span className="text-white">Provider Assigned</span>
                  <p className="text-xs text-slate-500">When a provider is on the way</p>
                </Label>
              </div>
              <Switch
                id="push_provider_assigned"
                checked={preferences.push_provider_assigned}
                onCheckedChange={() => handleToggle('push_provider_assigned')}
                data-testid="toggle-push-provider"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="h-4 w-4 text-green-400" />
                <Label htmlFor="push_chat_messages" className="cursor-pointer">
                  <span className="text-white">Chat Messages</span>
                  <p className="text-xs text-slate-500">New messages from providers</p>
                </Label>
              </div>
              <Switch
                id="push_chat_messages"
                checked={preferences.push_chat_messages}
                onCheckedChange={() => handleToggle('push_chat_messages')}
                data-testid="toggle-push-chat"
              />
            </div>
          </div>
        </div>
        
        <Separator className="bg-slate-800" />
        
        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchPreferences}
            className="border-slate-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button
            size="sm"
            onClick={savePreferences}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="save-notification-prefs"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Preferences
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
